import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { ArrowRight, BedDouble, KeyRound, Receipt, MessageSquare, Calendar, CheckCircle2 } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * PMS Experience — large floating calendar + operational widgets in editorial layout.
 */
export default function PMSExperience() {
  const t = useT();
  return (
    <section className="relative overflow-hidden bg-surface-1" data-testid="pms-experience">
      <div className="absolute inset-0 bg-dot opacity-40 mask-fade-y" aria-hidden />
      <div className="absolute -top-10 -left-20 h-[360px] w-[360px] rounded-full bg-[#FF7A00]/8 blur-3xl" aria-hidden />

      <div className="container-akm relative section">
        <div className="grid items-end gap-6 md:grid-cols-12">
          <div className="md:col-span-7">
            <div className="eyebrow">{t("PMS · Sistemi i Rezervimeve", "PMS · Reservations System")}</div>
            <h2 className="h2-akm mt-3">
              {t("Recepsioni, dhomat dhe pastrimi — të lidhura.", "Front desk, rooms and housekeeping — all connected.")}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="lead">
              {t(
                "Çdo pjesë e ekipit shikon të njëjtin informacion. Pa Excel. Pa kalim mes disa sistemeve. Akomoda lidh çdo veprim të hotelit — nga check-in te dhoma e gatshme për klientin tjetër.",
                "Every part of the team sees the same information. No spreadsheets. No jumping between systems. Akomoda ties together every hotel action — from check-in to the room being ready for the next guest."
              )}
            </p>
          </div>
        </div>

        <div className="relative mt-16">
          <div className="relative">
            <div className="absolute -inset-x-10 -bottom-14 -top-6 -z-10 rounded-[32px] bg-[radial-gradient(60%_60%_at_50%_100%,rgba(255,122,0,0.18),transparent_70%)] blur-2xl" aria-hidden />
            <div className="pointer-events-none absolute -inset-2 -z-10 rounded-[26px] border border-[#E2E8F0]" aria-hidden />

            <div className="float-slow overflow-hidden rounded-2xl border border-line bg-white shadow-cinematic-light">
              <div className="flex items-center justify-between border-b border-line bg-gradient-to-b from-[#FCFDFE] to-[#FAFBFC] px-5 py-3">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-[#FF7A00]" />
                  <span className="font-display text-[14px] font-bold text-ink">
                    {t("Kalendari · Java 47", "Calendar · Week 47")}
                  </span>
                  <span className="text-[12px] text-slate3">{t("· 18–24 Nëntor 2026", "· 18–24 Nov 2026")}</span>
                </div>
                <div className="flex items-center gap-2 text-[11.5px]">
                  <span className="rounded-md border border-line bg-white px-2.5 py-1 font-semibold text-slate2">
                    {t("Filtri", "Filters")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 font-semibold text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 pulse-soft" /> {t("Sinkronizim aktiv", "Sync active")}
                  </span>
                </div>
              </div>

              <LargeCalendarGrid t={t} />
            </div>

            <div className="pointer-events-none absolute inset-x-0 -bottom-[48%] hidden h-[48%] md:block" aria-hidden>
              <div className="reflection-light overflow-hidden rounded-2xl border border-line bg-white">
                <div className="h-14 border-b border-line bg-[#FAFBFC]" />
                <div className="h-40 bg-white" />
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:mt-8 md:grid-cols-12">
            <FloatCard icon={KeyRound} title={t("Recepsioni","Front desk")} hue="#FF7A00" className="md:col-span-3">
              <ul className="mt-2 space-y-1 text-[12.5px] text-slate2">
                {[
                  t("Check-in i shpejtë","Fast check-in"),
                  t("Check-in online për klientët","Online check-in for guests"),
                  t("Verifikim i dokumentit","ID verification"),
                  t("Faturë me një klikim","One-click invoicing"),
                ].map((tt) => (
                  <li key={tt} className="flex items-start gap-1.5"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-[#FF7A00]" /> {tt}</li>
                ))}
              </ul>
            </FloatCard>

            <FloatCard icon={BedDouble} title={t("Pastrimi","Housekeeping")} hue="#0EA5E9" className="md:col-span-3">
              <div className="mt-2 space-y-1.5">
                {[
                  ["101", t("E pastruar","Cleaned"),  "#DCFCE7", "#166534"],
                  ["102", t("Në pastrim","Cleaning"), "#FEF3C7", "#92400E"],
                  ["202", "Out of Order",             "#E2E8F0", "#475569"],
                ].map(([n, s, bg, c]) => (
                  <div key={n} className="flex items-center justify-between rounded-md border border-line p-1.5 text-[11px]">
                    <span className="font-bold text-ink">{t("Dhoma","Room")} {n}</span>
                    <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: bg, color: c }}>{s}</span>
                  </div>
                ))}
              </div>
            </FloatCard>

            <FloatCard icon={Receipt} title={t("POS · Bar Onufri","POS · Onufri Bar")} hue="#16A34A" className="md:col-span-3">
              <div className="mt-2 space-y-1 text-[11.5px]">
                {[
                  ["Espresso x2", "€3.00"],
                  [t("Birrë Tirana","Tirana Beer"), "€3.00"],
                  [t("Sallatë Greke","Greek Salad"), "€6.50"],
                ].map(([n, p]) => (
                  <div key={n} className="flex items-center justify-between border-b border-dashed border-line py-1 text-slate2 last:border-b-0">
                    <span>{n}</span>
                    <span className="font-display font-bold text-ink">{p}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1 text-[12px]">
                  <span className="text-slate3">{t("Dhoma 201","Room 201")}</span>
                  <span className="font-display text-[13px] font-extrabold text-ink">€12.50</span>
                </div>
              </div>
            </FloatCard>

            <FloatCard icon={MessageSquare} title={t("Komunikimi me klientin","Guest communication")} hue="#8B5CF6" className="md:col-span-3">
              <div className="mt-2 space-y-1.5 text-[11.5px]">
                <div className="rounded-md bg-[#F1F5F9] px-2 py-1.5 text-slate2">
                  <span className="font-semibold text-ink">L. Mema</span> · {t("iu dërgua email-i para mbërritjes","pre-arrival email sent")}
                </div>
                <div className="rounded-md bg-[#FFEEDB] px-2 py-1.5 text-[#9A3412]">
                  <span className="font-semibold">{t("Kërkesë WhatsApp","WhatsApp request")}</span> · {t("check-in më herët","early check-in")}
                </div>
                <div className="rounded-md bg-[#DCFCE7] px-2 py-1.5 text-[#166534]">
                  <span className="font-semibold">{t("SMS konfirmimi","SMS confirmation")}</span> · {t("u dërgua","sent")}
                </div>
              </div>
            </FloatCard>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/pms" className="btn-primary">{t("Më shumë për PMS-in","More about the PMS")} <ArrowRight size={14} /></Link>
          <Link to="/housekeeping" className="btn-ghost">{t("Pastrimi i dhomave →","Housekeeping →")}</Link>
        </div>
      </div>
    </section>
  );
}

function FloatCard({ icon: Icon, title, hue, className = "", children }) {
  return (
    <div className={`card-elevated p-5 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${hue}15`, color: hue }}>
          <Icon size={15} />
        </span>
        <span className="font-display text-[14px] font-bold text-ink">{title}</span>
      </div>
      {children}
    </div>
  );
}

function LargeCalendarGrid({ t }) {
  const rooms = [
    { n: "101", ty: t("Standard","Standard"), status: "ok" },
    { n: "102", ty: t("Standard","Standard"), status: "clean" },
    { n: "103", ty: t("Standard","Standard"), status: "ok" },
    { n: "201", ty: t("Deluxe","Deluxe"),     status: "ok" },
    { n: "202", ty: t("Deluxe","Deluxe"),     status: "ooo" },
    { n: "301", ty: t("Suite","Suite"),       status: "ok" },
    { n: "302", ty: t("Suite","Suite"),       status: "ok" },
  ];
  const days = t(
    ["Hën 18", "Mar 19", "Mër 20", "Enj 21", "Pre 22", "Sht 23", "Die 24"],
    ["Mon 18", "Tue 19", "Wed 20", "Thu 21", "Fri 22", "Sat 23", "Sun 24"]
  );
  const bookings = [
    [0, 0, 3, "M. Hoxha",    "Booking.com", "#003580"],
    [0, 4, 2, "A. Krasniqi", t("Direkt","Direct"), "#16A34A"],
    [1, 1, 4, "L. Mema",     "Airbnb",      "#FF5A5F"],
    [2, 2, 5, "E. Berisha",  "Expedia",     "#0072CE"],
    [3, 0, 2, "I. Tafa",     "Booking.com", "#003580"],
    [3, 3, 4, "G. Doci",     t("Direkt","Direct"), "#16A34A"],
    [4, 5, 2, "F. Lleshi",   "Agoda",       "#5392F9"],
    [5, 1, 6, "R. Pano",     t("Direkt","Direct"), "#16A34A"],
    [6, 2, 4, "S. Kola",     "Vrbo",        "#245ABF"],
  ];
  const tint = (hex) => `${hex}22`;
  const statusColor = { ok: "transparent", clean: "#FEF3C7", ooo: "#E2E8F0" };

  return (
    <div className="grid grid-cols-[112px_repeat(7,minmax(0,1fr))] text-[11px]">
      <div className="border-b border-r border-line bg-[#FAFBFC] px-3 py-3 font-semibold text-slate3">
        {t("Dhoma","Room")}
      </div>
      {days.map((d, i) => (
        <div key={d} className={`border-b border-line bg-[#FAFBFC] px-2 py-3 text-center font-semibold text-slate3 ${i === 6 ? "" : "border-r"}`}>
          {d}
        </div>
      ))}
      {rooms.map((r, ri) => (
        <React.Fragment key={r.n}>
          <div className="border-b border-r border-line px-3 py-3" style={{ background: statusColor[r.status] || "transparent" }}>
            <div className="font-display text-[13px] font-bold text-ink">{r.n}</div>
            <div className="text-[10px] text-slate3">{r.ty}</div>
          </div>
          {days.map((_, di) => {
            const book = bookings.find(([rr, sd, sp]) => rr === ri && di >= sd && di < sd + sp);
            const isStart = book && book[1] === di;
            return (
              <div key={di} className={`relative border-b border-line ${di === 6 ? "" : "border-r"} cal-cell h-14`}>
                {isStart && (
                  <div
                    className="absolute inset-y-2 left-1 flex items-center gap-1.5 overflow-hidden rounded-md border px-2 text-[10.5px] font-semibold leading-tight"
                    style={{ width: `calc(${book[2] * 100}% - 8px)`, background: tint(book[5]), borderColor: `${book[5]}40`, color: book[5] }}
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: book[5] }} />
                    <span className="truncate">{book[3]} <span className="text-slate3 font-normal">· {book[4]}</span></span>
                  </div>
                )}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
